import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { addDoc, collection, deleteDoc, deleteField, doc, onSnapshot, orderBy, serverTimestamp, updateDoc, writeBatch } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { useCouple } from "../hooks/useCouple";
import { useCoupleCollection } from "../hooks/useCoupleCollection";
import { Sheet } from "../components/Sheet";
import type { LinkBoard, SavedLink } from "../types";

const fetchLinkPreview = httpsCallable(functions, "fetchLinkPreview");

type LinkSort = "added-desc" | "added-asc" | "rating-desc" | "alpha";
const SORT_OPTIONS: { value: LinkSort; label: string }[] = [
  { value: "added-desc", label: "Zuletzt hinzugefügt" },
  { value: "added-asc", label: "Zuerst hinzugefügt" },
  { value: "rating-desc", label: "Beste Bewertung" },
  { value: "alpha", label: "Alphabetisch" },
];
function sortStorageKey(boardId: string) {
  return `wir_links_sort_${boardId}`;
}
function loadSort(boardId: string): LinkSort {
  try {
    const raw = localStorage.getItem(sortStorageKey(boardId));
    return SORT_OPTIONS.some((o) => o.value === raw) ? (raw as LinkSort) : "added-asc";
  } catch {
    return "added-asc";
  }
}
// Unrated links sort to the bottom of "Beste Bewertung" rather than tying
// with a real 0 — -1 can never be a real average of 1–10 ratings.
function avgRating(l: SavedLink): number {
  const values = Object.values(l.ratings ?? {}).map((r) => r.value);
  return values.length ? values.reduce((a, b) => a + b, 0) / values.length : -1;
}

async function deleteBoard(coupleId: string, boardId: string, links: SavedLink[]) {
  for (let i = 0; i < links.length; i += 450) {
    const batch = writeBatch(db);
    for (const link of links.slice(i, i + 450)) batch.delete(doc(db, "couples", coupleId, "linkBoards", boardId, "links", link.id));
    await batch.commit();
  }
  await deleteDoc(doc(db, "couples", coupleId, "linkBoards", boardId));
}

function normalizeUrl(raw: string): string {
  const trimmed = raw.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function LinkBoardDetail() {
  const { boardId } = useParams<{ boardId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { couple, profile } = useCouple();
  const coupleId = couple?.id ?? null;

  const [board, setBoard] = useState<LinkBoard | null>(null);
  const [boardLoading, setBoardLoading] = useState(true);
  useEffect(() => {
    if (!coupleId || !boardId) return;
    setBoardLoading(true);
    return onSnapshot(doc(db, "couples", coupleId, "linkBoards", boardId), (snap) => {
      setBoard(snap.exists() ? ({ id: snap.id, ...snap.data() } as LinkBoard) : null);
      setBoardLoading(false);
    });
  }, [coupleId, boardId]);

  const { items: links, loading } = useCoupleCollection<SavedLink>(
    coupleId,
    boardId ? `linkBoards/${boardId}/links` : "linkBoards/_/links",
    orderBy("createdAt", "asc")
  );

  const [sort, setSort] = useState<LinkSort>("added-asc");
  useEffect(() => {
    if (boardId) setSort(loadSort(boardId));
  }, [boardId]);
  const [sortOpen, setSortOpen] = useState(false);
  function chooseSort(value: LinkSort) {
    setSort(value);
    if (boardId) {
      try {
        localStorage.setItem(sortStorageKey(boardId), value);
      } catch {
        // Best-effort — worst case the choice doesn't survive a reload.
      }
    }
    setSortOpen(false);
  }
  const sortedLinks = useMemo(() => {
    if (sort === "added-desc") return [...links].reverse();
    if (sort === "rating-desc") return [...links].sort((a, b) => avgRating(b) - avgRating(a));
    if (sort === "alpha") return [...links].sort((a, b) => (a.title || a.url).localeCompare(b.title || b.url));
    return links;
  }, [links, sort]);

  // For the "move to another board" action — every other board the couple
  // has, so a link can be relocated without deleting and re-adding it.
  const { items: allBoards } = useCoupleCollection<LinkBoard>(coupleId, "linkBoards", orderBy("createdAt", "asc"));
  const otherBoards = useMemo(() => allBoards.filter((b) => b.id !== boardId), [allBoards, boardId]);

  const [url, setUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    if (submitting || !url.trim() || !user || !profile || !coupleId || !boardId) return;
    setSubmitting(true);
    try {
      const normalized = normalizeUrl(url);
      const ref = await addDoc(collection(db, "couples", coupleId, "linkBoards", boardId, "links"), {
        url: normalized,
        title: null,
        description: null,
        image: null,
        siteName: null,
        previewStatus: "pending",
        createdByUid: user.uid,
        createdByName: profile.displayName,
        createdAt: serverTimestamp(),
      });
      setUrl("");
      // Best-effort: if this fails (site unreachable, blocks bots, etc.) the
      // link is still saved with previewStatus "pending" forever, which the
      // card renders the same as "error" — nothing is lost, it just shows
      // the raw url instead of a rich preview.
      fetchLinkPreview({ coupleId, boardId, linkId: ref.id, url: normalized }).catch(() => {});
    } finally {
      setSubmitting(false);
    }
  }

  const [manageOpen, setManageOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  function openManage() {
    setNewName(board?.name ?? "");
    setRenaming(false);
    setConfirmDelete(false);
    setManageOpen(true);
  }

  async function handleRename(e: FormEvent) {
    e.preventDefault();
    if (!newName.trim() || !coupleId || !boardId) return;
    await updateDoc(doc(db, "couples", coupleId, "linkBoards", boardId), { name: newName.trim() });
    setManageOpen(false);
  }

  async function handleDeleteBoard() {
    if (!coupleId || !boardId) return;
    await deleteBoard(coupleId, boardId, links);
    navigate("/links", { replace: true });
  }

  async function removeLink(linkId: string) {
    if (!coupleId || !boardId) return;
    await deleteDoc(doc(db, "couples", coupleId, "linkBoards", boardId, "links", linkId));
  }

  // Replaces the delete-and-re-add dance (open the link, copy it, delete
  // the entry here, paste it fresh into the target board) with one step —
  // keeps the rating/comments/preview, just relocates the document.
  // createdAt resets to now, so it lands at the "just added" end of
  // whichever sort the destination board is using.
  async function moveLink(link: SavedLink, targetBoardId: string) {
    if (!coupleId || !boardId) return;
    const { id, createdAt, ...rest } = link;
    const batch = writeBatch(db);
    batch.set(doc(db, "couples", coupleId, "linkBoards", targetBoardId, "links", id), { ...rest, createdAt: serverTimestamp() });
    batch.delete(doc(db, "couples", coupleId, "linkBoards", boardId, "links", id));
    await batch.commit();
    closeLinkMenu();
  }

  const [menuLinkId, setMenuLinkId] = useState<string | null>(null);
  const [menuMode, setMenuMode] = useState<"actions" | "move">("actions");
  const menuLink = links.find((l) => l.id === menuLinkId) ?? null;
  function openLinkMenu(link: SavedLink) {
    setMenuLinkId(link.id);
    setMenuMode("actions");
  }
  function closeLinkMenu() {
    setMenuLinkId(null);
  }

  async function retryPreview(link: SavedLink) {
    if (!coupleId || !boardId) return;
    await updateDoc(doc(db, "couples", coupleId, "linkBoards", boardId, "links", link.id), { previewStatus: "pending" });
    fetchLinkPreview({ coupleId, boardId, linkId: link.id, url: link.url }).catch(() => {});
  }

  const [ratingLinkId, setRatingLinkId] = useState<string | null>(null);
  const ratingLink = links.find((l) => l.id === ratingLinkId) ?? null;
  const myRating = user ? ratingLink?.ratings?.[user.uid]?.value ?? null : null;

  async function setRating(value: number | null) {
    if (!coupleId || !boardId || !ratingLinkId || !user || !profile) return;
    // Dot-path field update, same reasoning as comments below: merges into
    // the ratings map so rating mine never touches the partner's score.
    const field = `ratings.${user.uid}`;
    await updateDoc(
      doc(db, "couples", coupleId, "linkBoards", boardId, "links", ratingLinkId),
      value != null ? { [field]: { value, name: profile.displayName } } : { [field]: deleteField() }
    );
    setRatingLinkId(null);
  }

  const [commentLinkId, setCommentLinkId] = useState<string | null>(null);
  const [commentDraft, setCommentDraft] = useState("");
  function openComment(link: SavedLink) {
    setCommentDraft((user && link.comments?.[user.uid]?.text) ?? "");
    setCommentLinkId(link.id);
  }
  async function saveComment(e: FormEvent) {
    e.preventDefault();
    if (!coupleId || !boardId || !commentLinkId || !user || !profile) return;
    // Dot-path field update: merges into the comments map instead of
    // replacing it, so writing my own comment never touches the partner's.
    const field = `comments.${user.uid}`;
    const text = commentDraft.trim();
    await updateDoc(
      doc(db, "couples", coupleId, "linkBoards", boardId, "links", commentLinkId),
      text ? { [field]: { text, name: profile.displayName } } : { [field]: deleteField() }
    );
    setCommentLinkId(null);
  }
  // A long comment used to just be truncated on the card with no way to
  // read the rest — fine for your own (editing shows the full text in the
  // textarea), but the partner's comment had no "full text" view at all
  // since you can't open edit on someone else's. This gives every comment
  // a tap-to-read-in-full destination regardless of whose it is.
  const [viewingComment, setViewingComment] = useState<{ name: string; text: string } | null>(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const scrollerRef = useRef<HTMLDivElement>(null);
  function handleScroll() {
    const el = scrollerRef.current;
    if (!el || links.length === 0) return;
    const cardWidth = el.scrollWidth / links.length;
    setActiveIndex(Math.round(el.scrollLeft / cardWidth));
  }

  return (
    <main className="mx-auto min-h-dvh max-w-md px-5 pb-28 pt-6">
      <Link to="/links" className="inline-flex items-center gap-1 text-sm font-medium text-plum-700 dark:text-night-accent">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-3.5 w-3.5">
          <path d="M15 5 8 12l7 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Links
      </Link>

      <div className="mt-2 flex items-center justify-between">
        <h1 className="flex items-center gap-2 font-display text-[1.75rem] font-semibold tracking-tight text-plum-900 dark:text-night-accent">
          <span>{board?.icon ?? "🔗"}</span>
          <span className="truncate">{boardLoading ? "…" : board?.name ?? "Sammlung"}</span>
        </h1>
        <button onClick={openManage} aria-label="Sammlung verwalten" className="flex-shrink-0 text-ink-400 dark:text-night-faint">
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
            <circle cx="5" cy="12" r="1.6" />
            <circle cx="12" cy="12" r="1.6" />
            <circle cx="19" cy="12" r="1.6" />
          </svg>
        </button>
      </div>

      <form onSubmit={handleAdd} className="mt-4 flex gap-2">
        <input
          type="text"
          placeholder="Link einfügen…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="h-12 flex-1 rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
        />
        <button
          type="submit"
          disabled={!url.trim() || submitting}
          aria-label="Link hinzufügen"
          className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-plum-700 text-cream-50 disabled:opacity-50"
        >
          {submitting ? (
            "…"
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
              <path d="M12 5v14M5 12h14" strokeLinecap="round" />
            </svg>
          )}
        </button>
      </form>

      {links.length > 1 && (
        <button onClick={() => setSortOpen(true)} className="mt-4 flex items-center gap-1.5 text-xs font-medium text-ink-600 dark:text-night-muted">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5">
            <path d="M6 7h8M6 12h5M6 17h2M17 5v14m0 0 3-3m-3 3-3-3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Sortieren: {SORT_OPTIONS.find((o) => o.value === sort)?.label}
        </button>
      )}

      {loading ? (
        <p className="mt-6 text-sm text-ink-400 dark:text-night-faint">Lädt…</p>
      ) : links.length === 0 ? (
        <p className="mt-6 text-sm text-ink-400 dark:text-night-faint">Noch keine Links gespeichert.</p>
      ) : (
        <>
          <div
            ref={scrollerRef}
            onScroll={handleScroll}
            className="-mx-5 mt-5 flex snap-x snap-mandatory gap-3 overflow-x-auto px-5 pb-2"
          >
            {sortedLinks.map((link) => (
              <LinkCard
                key={link.id}
                link={link}
                meUid={user?.uid ?? null}
                onMenu={() => openLinkMenu(link)}
                onRate={() => setRatingLinkId(link.id)}
                onComment={() => openComment(link)}
                onViewComment={(c) => setViewingComment(c)}
                onRetryPreview={() => retryPreview(link)}
              />
            ))}
          </div>
          {links.length > 1 && (
            <div className="mt-3 flex justify-center gap-1.5">
              {links.map((_, i) => (
                <span key={i} className={`h-1.5 w-1.5 rounded-full ${i === activeIndex ? "bg-plum-700" : "bg-cream-200 dark:bg-night-border"}`} />
              ))}
            </div>
          )}
          <p className="mt-2 text-center text-xs text-ink-400 dark:text-night-faint">Zum Vergleichen nach links/rechts wischen</p>
        </>
      )}

      <Sheet open={manageOpen} onClose={() => setManageOpen(false)} title="Sammlung verwalten">
        {confirmDelete ? (
          <div>
            <p className="text-sm text-ink-600 dark:text-night-muted">
              „{board?.name}" wird mit allen {links.length} Links unwiderruflich gelöscht.
            </p>
            <div className="mt-5 flex gap-3">
              <button onClick={() => setConfirmDelete(false)} className="h-11 flex-1 rounded-xl border border-cream-200 text-sm font-semibold text-ink-900 dark:border-night-border dark:text-night-text">
                Abbrechen
              </button>
              <button onClick={handleDeleteBoard} className="h-11 flex-1 rounded-xl bg-garnet-600 text-sm font-semibold text-cream-50">
                Endgültig löschen
              </button>
            </div>
          </div>
        ) : renaming ? (
          <form onSubmit={handleRename} className="flex gap-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="h-12 flex-1 rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
            />
            <button type="submit" disabled={!newName.trim()} className="h-12 flex-shrink-0 rounded-xl bg-plum-700 px-4 text-sm font-semibold text-cream-50 disabled:opacity-50">
              OK
            </button>
          </form>
        ) : (
          <div className="space-y-2">
            <button
              onClick={() => setRenaming(true)}
              className="flex h-12 w-full items-center rounded-xl px-3 text-left text-sm font-medium text-ink-900 hover:bg-cream-100 dark:text-night-text dark:hover:bg-night-surface-raised"
            >
              Sammlung umbenennen
            </button>
            <button
              onClick={() => setConfirmDelete(true)}
              className="flex h-12 w-full items-center rounded-xl px-3 text-left text-sm font-medium text-garnet-600 hover:bg-garnet-100 dark:hover:bg-garnet-600/15"
            >
              Sammlung löschen
            </button>
          </div>
        )}
      </Sheet>

      <Sheet open={ratingLinkId !== null} onClose={() => setRatingLinkId(null)} title="Bewertung (1–10)">
        <p className="truncate text-sm text-ink-600 dark:text-night-muted">{ratingLink?.title || ratingLink?.url}</p>
        <div className="mt-4 grid grid-cols-5 gap-2">
          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              onClick={() => setRating(n)}
              className={`flex h-11 items-center justify-center rounded-xl text-sm font-semibold ${
                myRating === n ? "bg-plum-700 text-cream-50" : "border border-cream-200 text-ink-900 dark:border-night-border dark:text-night-text"
              }`}
            >
              {n}
            </button>
          ))}
        </div>
        {myRating != null && (
          <button onClick={() => setRating(null)} className="mt-3 h-11 w-full rounded-xl border border-cream-200 text-sm font-semibold text-ink-900 dark:border-night-border dark:text-night-text">
            Meine Bewertung entfernen
          </button>
        )}
      </Sheet>

      <Sheet open={commentLinkId !== null} onClose={() => setCommentLinkId(null)} title="Kommentar">
        <form onSubmit={saveComment} className="space-y-3">
          <textarea
            placeholder="z.B. Rezept mit weniger Salz gemacht, oder wie das Restaurant war…"
            value={commentDraft}
            onChange={(e) => setCommentDraft(e.target.value)}
            rows={4}
            className="w-full rounded-xl border border-cream-200 bg-white px-4 py-3 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
          />
          <button type="submit" className="h-11 w-full rounded-xl bg-plum-700 text-sm font-semibold text-cream-50">
            Speichern
          </button>
        </form>
      </Sheet>

      <Sheet open={viewingComment !== null} onClose={() => setViewingComment(null)} title={viewingComment?.name ?? "Kommentar"}>
        <p className="whitespace-pre-wrap text-sm text-ink-900 dark:text-night-text">{viewingComment?.text}</p>
      </Sheet>

      <Sheet open={sortOpen} onClose={() => setSortOpen(false)} title="Sortieren">
        <div className="space-y-1.5">
          {SORT_OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => chooseSort(o.value)}
              className={`flex h-12 w-full items-center justify-between rounded-xl px-3 text-left text-sm font-medium ${
                sort === o.value ? "bg-plum-100 text-plum-700 dark:bg-night-surface-raised dark:text-night-accent" : "text-ink-900 dark:text-night-text"
              }`}
            >
              {o.label}
              {sort === o.value && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="h-4 w-4">
                  <path d="M5 12.5 9.5 17 19 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          ))}
        </div>
      </Sheet>

      <Sheet
        open={menuLinkId !== null}
        onClose={closeLinkMenu}
        title={menuMode === "move" ? "Verschieben nach…" : menuLink?.title || menuLink?.url || "Link"}
      >
        {menuMode === "move" ? (
          otherBoards.length === 0 ? (
            <p className="text-sm text-ink-400 dark:text-night-faint">Keine andere Sammlung vorhanden.</p>
          ) : (
            <div className="space-y-1.5">
              {otherBoards.map((b) => (
                <button
                  key={b.id}
                  onClick={() => menuLink && moveLink(menuLink, b.id)}
                  className="flex h-12 w-full items-center gap-2.5 rounded-xl px-3 text-left text-sm font-medium text-ink-900 hover:bg-cream-100 dark:text-night-text dark:hover:bg-night-surface-raised"
                >
                  <span>{b.icon}</span> {b.name}
                </button>
              ))}
            </div>
          )
        ) : (
          <div className="space-y-2">
            <button
              onClick={() => setMenuMode("move")}
              className="flex h-12 w-full items-center rounded-xl px-3 text-left text-sm font-medium text-ink-900 hover:bg-cream-100 dark:text-night-text dark:hover:bg-night-surface-raised"
            >
              In andere Sammlung verschieben
            </button>
            <button
              onClick={() => {
                if (menuLinkId) removeLink(menuLinkId);
                closeLinkMenu();
              }}
              className="flex h-12 w-full items-center rounded-xl px-3 text-left text-sm font-medium text-garnet-600 hover:bg-garnet-100 dark:hover:bg-garnet-600/15"
            >
              Entfernen
            </button>
          </div>
        )}
      </Sheet>
    </main>
  );
}

function LinkCard({
  link,
  meUid,
  onMenu,
  onRate,
  onComment,
  onViewComment,
  onRetryPreview,
}: {
  link: SavedLink;
  meUid: string | null;
  onMenu: () => void;
  onRate: () => void;
  onComment: () => void;
  onViewComment: (comment: { name: string; text: string }) => void;
  onRetryPreview: () => void;
}) {
  const comments = Object.entries(link.comments ?? {});
  const haveMyComment = !!meUid && !!link.comments?.[meUid];
  // Both ratings show side by side so it's clear who found it how — colored
  // the same relative way as the Todos assignee dots (plum = mine, gold =
  // partner's), rather than two anonymous numbers.
  const ratings = Object.entries(link.ratings ?? {});
  return (
    <div className="card relative flex w-[85%] flex-shrink-0 snap-center flex-col overflow-hidden rounded-2xl">
      <button
        onClick={onRate}
        aria-label={ratings.length > 0 ? `Bewertungen: ${ratings.map(([, r]) => `${r.name} ${r.value}`).join(", ")}` : "Bewerten"}
        className="absolute left-2 top-2 z-10 flex items-center gap-1"
      >
        {ratings.length === 0 ? (
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ink-900/40 text-cream-50 backdrop-blur">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
              <path d="m12 4 2.3 4.9 5.2.7-3.8 3.7.9 5.3L12 16l-4.6 2.6.9-5.3-3.8-3.7 5.2-.7Z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        ) : (
          ratings.map(([uid, r]) => (
            <span
              key={uid}
              className={`flex h-8 items-center gap-1 rounded-full px-2 backdrop-blur ${uid === meUid ? "bg-plum-700 text-cream-50" : "bg-gold-500 text-ink-900"}`}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
                <path d="m12 4 2.3 4.9 5.2.7-3.8 3.7.9 5.3L12 16l-4.6 2.6.9-5.3-3.8-3.7 5.2-.7Z" />
              </svg>
              <span className="text-xs font-bold">{r.value}</span>
            </span>
          ))
        )}
      </button>
      <button
        onClick={onMenu}
        aria-label="Weitere Aktionen"
        className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-ink-900/40 text-cream-50 backdrop-blur"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
          <circle cx="12" cy="5" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="12" cy="19" r="1.6" />
        </svg>
      </button>

      {link.previewStatus === "pending" ? (
        <div className="skeleton aspect-[4/3] w-full" />
      ) : link.image ? (
        <img src={link.image} alt="" className="aspect-[4/3] w-full object-cover" />
      ) : (
        <div className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-2 bg-cream-100 dark:bg-night-surface-raised">
          <span className="text-3xl">🔗</span>
          <button onClick={onRetryPreview} className="text-xs font-medium text-plum-700 dark:text-night-accent">
            Vorschau neu laden
          </button>
        </div>
      )}

      <div className="flex flex-1 flex-col p-4">
        {link.previewStatus === "pending" ? (
          <>
            <div className="skeleton h-4 w-3/4 rounded" />
            <div className="skeleton mt-2 h-3 w-1/2 rounded" />
          </>
        ) : (
          <>
            <p className="line-clamp-2 text-sm font-semibold text-ink-900 dark:text-night-text">{link.title || link.url}</p>
            {link.siteName && <p className="mt-0.5 truncate text-xs text-ink-400 dark:text-night-faint">{link.siteName}</p>}
            {link.description && <p className="mt-1.5 line-clamp-3 text-xs text-ink-600 dark:text-night-muted">{link.description}</p>}
            {comments.length > 0 && (
              <div className="mt-2 space-y-1">
                {comments.map(([uid, c]) => (
                  <button
                    key={uid}
                    onClick={() => (uid === meUid ? onComment() : onViewComment(c))}
                    className="line-clamp-2 block w-full text-left text-xs text-ink-600 dark:text-night-muted"
                  >
                    <span className="font-semibold text-ink-900 dark:text-night-text">{c.name}: </span>
                    <span className="italic">„{c.text}"</span>
                  </button>
                ))}
              </div>
            )}
            <button onClick={onComment} className="mt-1.5 text-left text-xs font-medium text-plum-700 dark:text-night-accent">
              {haveMyComment ? "Kommentar bearbeiten" : "+ Kommentar"}
            </button>
          </>
        )}
        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 flex h-10 w-full flex-shrink-0 items-center justify-center rounded-xl bg-plum-700 text-sm font-semibold text-cream-50"
        >
          Öffnen
        </a>
      </div>
    </div>
  );
}
